// view_assets.js

import NavBar from '@/components/NavBar/NavBar.vue';
import Swal from 'sweetalert2';
import { format_date } from '@/utils/date_formatter';
import { API_BASE_URL } from "@/config";
import { validateToken } from '@/utils/auth';
import { LOGOUT } from "@/utils/logout.js";

export default {
  name: 'ViewAsset',
  mixins: [LOGOUT],
  components: {
    NavBar,
  },
  data() {
    return {
      asset_details: {
        id: '',
        asset_name: '',
        asset_description: '',
        asset_type: '',       // Ensure this is initialized appropriately
        date_created: '',
        created_asset_id: null,
      },
      asset_file: null,        // To store the selected image file
      imageUrl: null,          // To store the image URL for preview
      asset_data: '',
      qr_code_data: {
        id: "",
        link: "",
        qr_code: "",
      },
      workflow_data: '',
      qr_link: '',             // Assuming this is used somewhere else
      success_message: '',     // For copy link operation
      error_messages: '',      // For copy link operation
      user_role: ''
    };
  },
  methods: {
    formatDate(date) {
      return format_date(date);
    },
    fetch_asset_details(asset_id) {
      const TOKEN = localStorage.getItem('token');
      const BASE_URL = API_BASE_URL;
      const GET_ASSET_DETAILS_URL = `${BASE_URL}/verify_api/workflow_management_api/get_asset_details_api/${asset_id}/`;

      $.ajax({
        method: 'GET',
        url: GET_ASSET_DETAILS_URL,
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        success: (response) => {
          if (response.status === 'success' && response.data) {
            this.asset_details = response.data;

            // Assign the image URL for display
            if (this.asset_details.uploaded_image_url) {
              this.imageUrl = this.asset_details.uploaded_image_url;
            } else {
              this.imageUrl = null;
            }

            // Correct assignment based on API response
            if (this.asset_details.asset_type__asset_type) {
              // If API provides asset_type__asset_type, use it
              this.asset_details.asset_type = this.asset_details.asset_type__asset_type;
            } else if (this.asset_details.asset_type && this.asset_details.asset_type.name) {
              // If asset_type is an object with a name property
              this.asset_details.asset_type = this.asset_details.asset_type.name;
            }
            // Else, asset_type is already correct or not available
          }
        },
        error: (error) => {
          Swal.fire({
            icon: 'error',
            text: error.responseJSON
              ? error.responseJSON.message
              : 'Failed to load asset details.',
            confirmButtonText: 'OK',
          });
        },
      });
    },
    download_qr_code() {
      if (!this.qr_code_data.qr_code) return;
      
      const TOKEN = localStorage.getItem('token');
      const BASE_URL = API_BASE_URL;
      const DOWNLOAD_URL = `${BASE_URL}/verify_api/workflow_management_api/download_qr_code/${this.qr_code_data.id}/`;

      // Use Swal directly instead of this.$swal
      Swal.fire({
          title: 'Downloading QR Code...',
          allowOutsideClick: false,
          showConfirmButton: false,
          didOpen: () => {
              Swal.showLoading();
          }
      });

      const xhr = new XMLHttpRequest();
      xhr.open('GET', DOWNLOAD_URL, true);
      xhr.setRequestHeader('Authorization', `Token ${TOKEN}`);
      xhr.responseType = 'blob';

      xhr.onload = () => {
          Swal.close(); // Use Swal directly

          if (xhr.status === 200) {
              const blob = new Blob([xhr.response], { type: 'image/png' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `${this.asset_details.asset_name}_qr_code.png`.toLowerCase().replace(/\s+/g, '_');
              document.body.appendChild(link);
              link.click();

              // Cleanup
              setTimeout(() => {
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
              }, 100);
          } else {
              Swal.fire({
                  icon: 'error',
                  text: 'Failed to download QR code.',
                  confirmButtonText: 'OK'
              });
          }
      };

      xhr.onerror = () => {
          Swal.close();
          Swal.fire({ 
              icon: 'error',
              text: 'Network error occurred while downloading QR code.',
              confirmButtonText: 'OK'
          });
      };

      xhr.send();
    },

    browse_file() {
      this.$refs.fileInput.click();
    },
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },
    handle_file_upload(event) {
      const file = event.target.files[0];

      // Validate file type
      if (file && !file.type.startsWith('image/')) {
        Swal.fire({
          icon: 'error',
          text: 'Please select a valid image file.',
        });
        return;
      }

      // Validate file size (e.g., max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file && file.size > maxSize) {
        Swal.fire({
          icon: 'error',
          text: 'File size exceeds 5MB limit.',
        });
        return;
      }

      this.asset_file = file;

      // Handle image preview
      if (this.asset_file && this.asset_file.type.startsWith('image/')) {
        // Revoke existing object URL if any
        if (this.imageUrl && this.imageUrl.startsWith('blob:')) {
          URL.revokeObjectURL(this.imageUrl);
        }
        // Create a new object URL for the uploaded image
        this.imageUrl = URL.createObjectURL(this.asset_file);
      } else {
        // If not an image file, reset the imageUrl and asset_file
        if (this.imageUrl && this.imageUrl.startsWith('blob:')) {
          URL.revokeObjectURL(this.imageUrl);
        }
        this.imageUrl = null;
        this.asset_file = null;
      }
    },

    edit_asset() {
      const TOKEN = localStorage.getItem('token');
      const BASE_URL = API_BASE_URL;
      const EDIT_ASSET_URL = `${BASE_URL}/verify_api/workflow_management_api/edit_asset_api/${this.asset_details.id}/`;

      const form_data = new FormData();
      form_data.append('asset_name', this.asset_details.asset_name);
      form_data.append('asset_description', this.asset_details.asset_description);
      form_data.append('asset_type', this.asset_details.asset_type);

      if (this.asset_file) {
        form_data.append('uploaded_image', this.asset_file);
      }

      $.ajax({
        method: 'POST',
        url: EDIT_ASSET_URL,
        data: form_data,
        contentType: false,
        processData: false,
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        success: (response) => {
          if (response.message === 'Asset with the same name already exists') {
            Swal.fire({
              icon: 'error',
              text: 'Asset with the same name already exists',
              confirmButtonText: 'OK',
            });
          } else {
            Swal.fire({
              icon: 'success',
              text: 'Asset updated successfully.',
              confirmButtonText: 'OK',
            }).then(() => {
              window.location.reload();
            });
          }
        },
        error: (error) => {
          Swal.fire({
            icon: 'error',
            text: error.responseJSON
              ? error.responseJSON.error
              : 'Failed to update asset.',
          });
        },
      });
    },
    view_asset(asset_id) {
      const TOKEN = localStorage.getItem('token');
      const BASE_URL = API_BASE_URL;
      const GET_ASSET_WORKFLOW_URL = `${BASE_URL}/verify_api/workflow_management_api/get_asset_workflows_api/${asset_id}/`;

      $.ajax({
        method: 'GET',
        url: GET_ASSET_WORKFLOW_URL,
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        success: (response) => {
          if (response.asset_workflows && response.asset_workflows.length > 0) {
            this.asset_data = response.asset_workflows[0].asset;
            this.qr_code_data = response.asset_workflows[0].qr_code;
            this.workflow_data = response.asset_workflows[0].workflow;

            const is_production = window.location.hostname !== 'localhost'
            if (this.qr_code_data.link == is_production) {
                this.qr_code_data.link = this.qr_code_data.link.replace("http://", "https://");
            }
          }
        },
        error: (error) => {
          Swal.fire({
            icon: 'error',
            text: error.responseJSON
              ? error.responseJSON.error
              : 'Failed to load asset workflow data.',
          });
        },
      });
    },
    copy_qr_link() {
      navigator.clipboard
        .writeText(this.qr_link)
        .then(() => {
          this.success_message = 'Link copied to clipboard!';
          this.error_messages = '';
        })
        .catch(() => {
          this.error_messages = 'Failed to copy the link.';
          this.success_message = '';
        });
    },
  },


  mounted() {
    if (!validateToken(this.$router)) {
      return;
    }
    const asset_id = this.$route.params.id;
    this.user_role = this.$store.getters.get_user_role;
    this.fetch_asset_details(asset_id);
    this.view_asset(asset_id);
  },
  beforeDestroy() {
    // Revoke the object URL when the component is destroyed
    if (this.imageUrl && this.imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.imageUrl);
    }
  },
};
